import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Inbox row in `alerts` for ticket activity (separate from `ticketLogs` activity stream).
 * Respects companies/{companyId}.notificationPrefs.ticketAlerts and ticketCommentAlerts.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} opts
 * @param {string} opts.companyId
 * @param {string} [opts.ticketId]
 * @param {string} opts.title
 * @param {string} opts.message
 * @param {'info'|'warning'|'critical'|'success'} [opts.severity]
 * @param {string} opts.ruleCode e.g. TICKET_CREATED
 * @param {'created'|'updated'|'comment'|'archived'|'restored'|'deleted'} opts.ticketAction
 * @param {boolean} [opts.notifySuperAdmins] — also write a role-targeted alert for super_admin
 * @param {string|null} [opts.actorUid]
 * @param {string} [opts.actorEmail]
 */
export async function appendTicketInboxAlert(db, opts) {
  const {
    companyId,
    ticketId,
    title,
    message,
    severity = 'info',
    ruleCode,
    ticketAction,
    notifySuperAdmins = false,
    actorUid = null,
    actorEmail = '',
  } = opts;

  if (!companyId || !title) return;

  let prefs = {};
  try {
    const co = await getDoc(doc(db, 'companies', companyId));
    prefs = co.exists() ? (co.data().notificationPrefs || {}) : {};
  } catch (e) {
    console.warn('appendTicketInboxAlert: could not read company prefs', e);
  }

  if (prefs.ticketAlerts === false) return;
  if (ticketAction === 'comment' && prefs.ticketCommentAlerts === false) return;

  const baseMeta = {
    ticketId: ticketId || null,
    source: 'tickets',
    ticketAction: ticketAction || null,
  };

  const row = {
    title,
    message: message || title,
    severity,
    status: 'active',
    targetType: 'company',
    targetCompanyId: companyId,
    targetRole: null,
    ruleCode: ruleCode || 'TICKET_EVENT',
    createdAt: serverTimestamp(),
    startsAt: serverTimestamp(),
    expiresAt: null,
    createdByUid: actorUid,
    createdByEmail: actorEmail || '',
    readBy: {},
    dismissedBy: {},
    meta: baseMeta,
  };

  await addDoc(collection(db, 'alerts'), row);

  if (notifySuperAdmins) {
    await addDoc(collection(db, 'alerts'), {
      title: `[Platform] ${title}`,
      message: message || title,
      severity: severity === 'critical' ? 'critical' : 'warning',
      status: 'active',
      targetType: 'role',
      targetCompanyId: null,
      targetRole: 'super_admin',
      ruleCode: `${ruleCode || 'TICKET_EVENT'}_SUPER_ADMIN`,
      createdAt: serverTimestamp(),
      startsAt: serverTimestamp(),
      expiresAt: null,
      createdByUid: actorUid,
      createdByEmail: actorEmail || '',
      readBy: {},
      dismissedBy: {},
      meta: { ...baseMeta, companyId, platform: true },
    });
  }
}
